// src/components/ui-components/SpecialRoleRoomCard.js

import { Box, Flex, Text, Button, Tooltip } from '@radix-ui/themes';
import React from 'react';
import { FaBell } from 'react-icons/fa';
import { IoShieldHalfOutline } from 'react-icons/io5';
import { PiBowlFoodFill } from 'react-icons/pi';
import { TbWashHand } from 'react-icons/tb';

const SpecialRoleRoomCard = ({ 
  bedData, 
  roleId,
  alarm = null,
  onConfirmAlarm,
  onCancelAlarm
}) => {

  // Check if the role-specific feature is enabled
  const isRoleFeatureEnabled = () => {
    switch (roleId) {
      case '5': return bedData.isSecurityCallEnabled; // Security
      case '6': return bedData.isLaunderyCallEnabled; // Housekeeping
      case '7': return bedData.isFoodCallEnabled; // F&B
      default: return true;
    }
  };

  const featureEnabled = isRoleFeatureEnabled();

  // Get role-specific icon and label with conditional styling
  const getRoleConfig = () => {
    const baseConfig = {
      color: featureEnabled ? 'text-green-700' : 'text-gray-400',
      bgColor: featureEnabled ? 'bg-green-50' : 'bg-gray-50',
      borderColor: featureEnabled ? 'border-green-200' : 'border-gray-200'
    };

    switch (roleId) {
      case '5':
        return {
          ...baseConfig,
          icon: <IoShieldHalfOutline size={32} />,
          label: 'Security'
        };
      case '6':
        return {
          ...baseConfig,
          icon: <TbWashHand size={32} />,
          label: 'Housekeeping'
        };
      case '7':
        return {
          ...baseConfig,
          icon: <PiBowlFoodFill size={32} />,
          label: 'F&B'
        };
      default:
        return {
          ...baseConfig,
          icon: <FaBell size={32} />,
          label: 'Task'
        };
    }
  };

  const roleConfig = getRoleConfig();

  const handleConfirmClick = () => {
    if (alarm && onConfirmAlarm) {
      onConfirmAlarm();
    }
  };

  const handleCancelClick = () => {
    if (alarm && onCancelAlarm) {
      onCancelAlarm();
    }
  };

  // Function to truncate text if too long
  const truncateText = (text, maxLength = 8) => {
    return text && text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Check if text needs truncation for tooltip
  const needsTooltip = (text, maxLength = 8) => {
    return text && text.length > maxLength;
  };

  return (
    <Box 
      className={`w-full shadow-md rounded-2xl transition-all duration-200 hover:shadow-lg ${
        alarm ? 'animate-pulse-enhanced' : ''
      }`}
    >
      <style>
        {`
          @keyframes pulse-enhanced {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7), 0 0 8px 2px rgba(255, 0, 0, 0.5);
            }
            50% {
              box-shadow: 0 0 0 10px rgba(255, 0, 0, 0.2), 0 0 16px 4px rgba(255, 0, 0, 0.8);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0), 0 0 8px 2px rgba(255, 0, 0, 0.5);
            }
          }
          .animate-pulse-enhanced {
            animation: pulse-enhanced 1.2s ease-in-out infinite;
          }
        `}
      </style>
      
      <div className="rounded-2xl bg-[#f6f5fa]">
        <Flex direction="column" gap="0" className="rounded-2xl">
          
          {/* Header with Room/Bed Info - Always visible with grey background */}
          <Flex
            justify="between"
            align="center"
            className="bg-[#efeeee] px-3 py-2 rounded-t-2xl"
          >
            {/* Room Number - Left aligned */}
            <Flex align="center">
              {needsTooltip(bedData.roomNumber) ? (
                <Tooltip content={`Room: ${bedData.roomNumber}`}>
                  <Text size="2" weight="medium" className="text-gray-700 cursor-help">
                    Room: <span className="font-semibold text-gray-900">{truncateText(bedData.roomNumber)}</span>
                  </Text>
                </Tooltip>
              ) : (
                <Text size="2" weight="medium" className="text-gray-700">
                  Room: <span className="font-semibold text-gray-900">{bedData.roomNumber}</span>
                </Text>
              )}
            </Flex>
            
            {/* Bed Number - Right aligned */}
            <Flex align="center">
              {needsTooltip(bedData.bedNumber) ? (
                <Tooltip content={`Bed: ${bedData.bedNumber}`}>
                  <Text size="2" weight="medium" className="text-gray-700 cursor-help">
                    Bed: <span className="font-semibold text-gray-900">{truncateText(bedData.bedNumber)}</span>
                  </Text>
                </Tooltip>
              ) : (
                <Text size="2" weight="medium" className="text-gray-700">
                  Bed: <span className="font-semibold text-gray-900">{bedData.bedNumber}</span>
                </Text>
              )}
            </Flex>
          </Flex>

          {alarm ? (
            // *** ALARM STATE - Maintain same card dimensions ***
            <Flex direction="column" className="p-4">
              {/* Role Icon Section with Alarm Color - Fixed height */}
              <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                className="rounded-xl border-2"
                style={{ 
                  backgroundColor: alarm.color,
                  borderColor: alarm.color,
                  minHeight: '140px' // Fixed height to match normal state
                }}
              >
                <Flex align="center" gap="2" className="mb-2">
                  <FaBell className="text-white" size={16} />
                  <Text weight="bold" size="3" className="text-white text-center">
                    {alarm.alarmName}
                  </Text>
                </Flex>
                
                <Box className="text-white mb-2">
                  {roleConfig.icon}
                </Box>
                
                {/* Show Room/Bed numbers instead of role label */}
                <Text size="3" weight="bold" className="text-white mb-3 bg-white/20 px-3 py-1 rounded-md">
                  {bedData.roomNumber}/{bedData.bedNumber}
                </Text>
                
                {/* Action Buttons */}
                <Flex gap="2">
                  <Button 
                    size="1" 
                    variant="soft" 
                    onClick={handleConfirmClick}
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30 transition-colors text-xs px-2 py-1"
                    data-bed-id={bedData.id}
                  >
                    Confirm
                  </Button>
                  <Button 
                    size="1" 
                    variant="soft" 
                    color="red"
                    onClick={handleCancelClick}
                    className="bg-red-500/20 hover:bg-red-500/30 text-white border-red-500/30 transition-colors text-xs px-2 py-1"
                    data-bed-id={bedData.id}
                  >
                    Cancel
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            // *** NORMAL STATE ***
            <Flex direction="column" className="p-4">
              {/* Role Icon Section with Green Theme - Matching height */}
              <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                className={`${roleConfig.bgColor} rounded-xl ${roleConfig.borderColor} border-2`}
                style={{ minHeight: '140px' }} // Same fixed height
              >
                <Box className={`${roleConfig.color} mb-3`}>
                  {roleConfig.icon}
                </Box>
                <Text size="3" weight="medium" className={featureEnabled ? "text-gray-800" : "text-gray-400"}>
                  {roleConfig.label}
                </Text>
                {!featureEnabled && (
                  <Text size="1" className="text-gray-400 mt-1">
                    Disabled
                  </Text>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      </div>
    </Box>
  );
};

export default SpecialRoleRoomCard;